// G-2 action-plan tasks on a Pursuit. This is a list, not a kanban and not
// the workstream board. Status is the task row's own open|in_progress|done|parked
// — never capture_projects.stage. Owner is an optional user id string, not a
// Person registry chip (G-3).
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SpecraButton, SpecraCard, SpecraCardContent, SpecraCardHeader, SpecraCardTitle, SpecraInput } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Check, Plus, Sparkles, X } from 'lucide-react';
import { useState } from 'react';
import { CAPTURE_PLAN_HEALTH_STATUSES, formatTs, labelOf, TASK_STATUSES, workstreamLabel } from './constants';
import { useCapturePlan, useDeleteCapturePlanArea, useDeleteCaptureTask, useSetCapturePlanAreaHealth, useSetCaptureTaskPlanArea, useUpsertCapturePlanArea, useUpsertCaptureTask } from './hooks';
const STATUS_STYLE = {
    open: 'bg-gray-100 text-gray-700',
    in_progress: 'bg-amber-50 text-amber-800',
    done: 'bg-green-50 text-green-700',
    parked: 'bg-indigo-50 text-indigo-700'
};
const HEALTH_STYLE = {
    not_started: 'bg-gray-100 text-gray-700',
    in_progress: 'bg-amber-50 text-amber-800',
    at_risk: 'bg-red-50 text-red-700',
    complete: 'bg-green-50 text-green-700',
    not_applicable: 'bg-indigo-50 text-indigo-700'
};
function dateInputValue(ts) {
    if (!ts) return '';
    const d = ts.toDate();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}
function parseDateInput(value) {
    if (!value) return null;
    const [y, m, d] = value.split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
}
// One Capture Plan area row: name/lead/target/summary, its health badge (set
// only through Save health below), an unapplied AI suggestion banner (Accept
// routes through the same Save path), and its linked tasks. Completing a
// linked task in the Action plan list above never touches any of this.
function CapturePlanAreaRow({ area, captureProjectId, tasks, correlationId }) {
    const setHealth = useSetCapturePlanAreaHealth(correlationId);
    const remove = useDeleteCapturePlanArea(correlationId);
    const [draftHealth, setDraftHealth] = useState(area.health || 'not_started');
    const [reason, setReason] = useState('');
    const suggestion = area.recentHealthEvents.find((e)=>e.origin === 'ai' && !e.applied);
    const areaTasks = tasks.filter((t)=>area.taskIds.includes(t.id));
    const saveHealth = (health, reasons)=>{
        setHealth.mutate({
            captureProjectId,
            areaId: area.id,
            health,
            reasons
        }, {
            onSuccess: ()=>setReason('')
        });
    };
    return /*#__PURE__*/ _jsxs("li", {
        className: "space-y-3 rounded-md border p-3 text-sm",
        "data-testid": Testids.capture.planAreaRow,
        "data-area-id": area.id,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-start justify-between gap-2",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "min-w-0 flex-1 space-y-0.5",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "font-medium",
                                children: area.name
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "text-xs text-muted-foreground",
                                children: [
                                    area.leadUserId || 'Unassigned',
                                    area.targetDate ? ` · target ${formatTs(area.targetDate)}` : ''
                                ]
                            }),
                            area.summary && /*#__PURE__*/ _jsx("p", {
                                className: "text-xs text-muted-foreground",
                                children: area.summary
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-2",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: `inline-flex items-center rounded-full px-2 py-0.5 text-xs ${HEALTH_STYLE[area.health] ?? HEALTH_STYLE.not_started}`,
                                "data-testid": Testids.capture.planAreaHealthBadge,
                                children: labelOf(CAPTURE_PLAN_HEALTH_STATUSES, area.health || 'not_started')
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                variant: "ghost",
                                size: "sm",
                                "data-testid": Testids.capture.planAreaDeleteButton,
                                disabled: remove.isPending,
                                onClick: ()=>remove.mutate({
                                        captureProjectId,
                                        id: area.id
                                    }),
                                iconLeft: /*#__PURE__*/ _jsx(X, {
                                    className: "h-3.5 w-3.5"
                                }),
                                children: "Remove"
                            })
                        ]
                    })
                ]
            }),
            suggestion && /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2 rounded-md border border-blue-200 bg-blue-50 p-2 text-xs",
                "data-testid": Testids.capture.planSuggestionBanner,
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-1 font-medium text-blue-800",
                        children: [
                            /*#__PURE__*/ _jsx(Sparkles, {
                                className: "h-3.5 w-3.5"
                            }),
                            "AI suggests ",
                            labelOf(CAPTURE_PLAN_HEALTH_STATUSES, suggestion.proposedHealth)
                        ]
                    }),
                    suggestion.reasons && /*#__PURE__*/ _jsx("p", {
                        className: "text-blue-700",
                        children: suggestion.reasons
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        variant: "secondary",
                        "data-testid": Testids.capture.planSuggestionAcceptButton,
                        disabled: setHealth.isPending,
                        onClick: ()=>saveHealth(suggestion.proposedHealth, suggestion.reasons),
                        children: "Accept"
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-end gap-2",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "w-40 space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx("label", {
                                className: "text-xs font-medium",
                                htmlFor: `capture-plan-health-${area.id}`,
                                children: "Health"
                            }),
                            /*#__PURE__*/ _jsxs(Select, {
                                value: draftHealth,
                                onValueChange: setDraftHealth,
                                children: [
                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                        id: `capture-plan-health-${area.id}`,
                                        "data-testid": Testids.capture.planAreaHealthSelect,
                                        children: /*#__PURE__*/ _jsx(SelectValue, {})
                                    }),
                                    /*#__PURE__*/ _jsx(SelectContent, {
                                        children: CAPTURE_PLAN_HEALTH_STATUSES.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                value: o.value,
                                                children: o.label
                                            }, o.value))
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "min-w-[180px] flex-1 space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx("label", {
                                className: "text-xs font-medium",
                                htmlFor: `capture-plan-reason-${area.id}`,
                                children: "Reason"
                            }),
                            /*#__PURE__*/ _jsx(SpecraInput, {
                                id: `capture-plan-reason-${area.id}`,
                                value: reason,
                                onChange: (e)=>setReason(e.target.value),
                                placeholder: "Why this health",
                                "data-testid": Testids.capture.planAreaHealthReasonInput
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        "data-testid": Testids.capture.planAreaHealthSaveButton,
                        disabled: setHealth.isPending,
                        onClick: ()=>saveHealth(draftHealth, reason),
                        children: "Save health"
                    })
                ]
            }),
            areaTasks.length > 0 && /*#__PURE__*/ _jsx("ul", {
                className: "space-y-1 border-t pt-2",
                children: areaTasks.map((t)=>/*#__PURE__*/ _jsxs("li", {
                        className: "flex items-center justify-between text-xs",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                children: t.title
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: `rounded-full px-2 py-0.5 ${STATUS_STYLE[t.status] ?? STATUS_STYLE.open}`,
                                children: labelOf(TASK_STATUSES, t.status)
                            })
                        ]
                    }, t.id))
            })
        ]
    });
}
export function CaptureTasksPanel({ captureProjectId, tasks, correlationId }) {
    const upsert = useUpsertCaptureTask(correlationId);
    const remove = useDeleteCaptureTask(correlationId);
    const [title, setTitle] = useState('');
    const [owner, setOwner] = useState('');
    const [due, setDue] = useState('');
    const plan = useCapturePlan(captureProjectId);
    const addArea = useUpsertCapturePlanArea(correlationId);
    const setTaskArea = useSetCaptureTaskPlanArea(correlationId);
    const [areaName, setAreaName] = useState('');
    const [areaLead, setAreaLead] = useState('');
    const [areaTarget, setAreaTarget] = useState('');
    const [areaSummary, setAreaSummary] = useState('');
    const areas = plan.data?.areas ?? [];
    const upcomingReviews = plan.data?.upcomingReviews ?? [];
    const upcomingDecisions = plan.data?.upcomingDecisions ?? [];
    const references = plan.data?.references;
    // The projection (area.taskIds), not a field on the task itself — a task
    // doesn't carry its own area id, only an area's taskIds names its tasks.
    const areaIdForTask = (taskId)=>areas.find((a)=>a.taskIds.includes(taskId))?.id ?? '';
    const submitArea = ()=>{
        const trimmed = areaName.trim();
        if (!trimmed) return;
        addArea.mutate({
            captureProjectId,
            name: trimmed,
            leadUserId: areaLead.trim(),
            targetDate: parseDateInput(areaTarget),
            summary: areaSummary.trim()
        }, {
            onSuccess: ()=>{
                setAreaName('');
                setAreaLead('');
                setAreaTarget('');
                setAreaSummary('');
            }
        });
    };
    const add = ()=>{
        const trimmed = title.trim();
        if (!trimmed) return;
        upsert.mutate({
            captureProjectId,
            title: trimmed,
            ownerUserId: owner.trim(),
            dueAt: parseDateInput(due),
            status: 'open'
        }, {
            onSuccess: ()=>{
                setTitle('');
                setOwner('');
                setDue('');
            }
        });
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(SpecraCard, {
                "data-testid": Testids.capture.tasksPanel,
                children: [
                    /*#__PURE__*/ _jsx(SpecraCardHeader, {
                        className: "py-4",
                        children: /*#__PURE__*/ _jsx(SpecraCardTitle, {
                            children: "Action plan"
                        })
                    }),
                    /*#__PURE__*/ _jsxs(SpecraCardContent, {
                        className: "space-y-3",
                        children: [
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-sm text-muted-foreground",
                                children: "Tasks on this pursuit — call Jane, draft CONOPS, send NDA. Not a pipeline board and not the workstream artifact tracker."
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex flex-wrap items-end gap-2",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "min-w-[180px] flex-1 space-y-1",
                                        children: [
                                            /*#__PURE__*/ _jsx("label", {
                                                className: "text-xs font-medium",
                                                htmlFor: "capture-task-title",
                                                children: "Title"
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraInput, {
                                                id: "capture-task-title",
                                                value: title,
                                                onChange: (e)=>setTitle(e.target.value),
                                                placeholder: "Call Jane",
                                                "data-testid": Testids.capture.tasksTitleInput
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "w-40 space-y-1",
                                        children: [
                                            /*#__PURE__*/ _jsx("label", {
                                                className: "text-xs font-medium",
                                                htmlFor: "capture-task-owner",
                                                children: "Owner"
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraInput, {
                                                id: "capture-task-owner",
                                                value: owner,
                                                onChange: (e)=>setOwner(e.target.value),
                                                placeholder: "User id",
                                                "data-testid": Testids.capture.tasksOwnerInput
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "w-40 space-y-1",
                                        children: [
                                            /*#__PURE__*/ _jsx("label", {
                                                className: "text-xs font-medium",
                                                htmlFor: "capture-task-due",
                                                children: "Due"
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraInput, {
                                                id: "capture-task-due",
                                                type: "date",
                                                value: due,
                                                onChange: (e)=>setDue(e.target.value),
                                                "data-testid": Testids.capture.tasksDueInput
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "sm",
                                        onClick: add,
                                        disabled: !title.trim() || upsert.isPending,
                                        "data-testid": Testids.capture.tasksAddButton,
                                        iconLeft: /*#__PURE__*/ _jsx(Plus, {
                                            className: "h-4 w-4"
                                        }),
                                        children: "Add"
                                    })
                                ]
                            }),
                            tasks.length === 0 ? /*#__PURE__*/ _jsx("p", {
                                className: "text-sm text-muted-foreground",
                                children: "No action-plan tasks yet."
                            }) : /*#__PURE__*/ _jsx("ul", {
                                className: "space-y-2",
                                children: tasks.map((t)=>/*#__PURE__*/ _jsxs("li", {
                                        className: "flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm",
                                        "data-testid": Testids.capture.tasksRow,
                                        "data-task-id": t.id,
                                        children: [
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "min-w-0 flex-1 space-y-0.5",
                                                children: [
                                                    /*#__PURE__*/ _jsx("div", {
                                                        className: "font-medium",
                                                        children: t.title
                                                    }),
                                                    /*#__PURE__*/ _jsxs("div", {
                                                        className: "text-xs text-muted-foreground",
                                                        children: [
                                                            t.ownerUserId || 'Unassigned',
                                                            t.dueAt ? ` · due ${formatTs(t.dueAt)}` : ''
                                                        ]
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                className: `inline-flex items-center rounded-full px-2 py-0.5 text-xs ${STATUS_STYLE[t.status] ?? STATUS_STYLE.open}`,
                                                children: labelOf(TASK_STATUSES, t.status)
                                            }),
                                            areas.length > 0 && /*#__PURE__*/ _jsxs(Select, {
                                                value: areaIdForTask(t.id),
                                                onValueChange: (areaId)=>setTaskArea.mutate({
                                                        captureProjectId,
                                                        taskId: t.id,
                                                        areaId
                                                    }),
                                                children: [
                                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                                        className: "h-8 w-36",
                                                        "data-testid": Testids.capture.tasksAreaSelect,
                                                        children: /*#__PURE__*/ _jsx(SelectValue, {
                                                            placeholder: "No area"
                                                        })
                                                    }),
                                                    /*#__PURE__*/ _jsx(SelectContent, {
                                                        children: areas.map((a)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                                value: a.id,
                                                                children: a.name
                                                            }, a.id))
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "flex items-center gap-1",
                                                children: [
                                                    t.status !== 'done' && /*#__PURE__*/ _jsx(SpecraButton, {
                                                        variant: "ghost",
                                                        size: "sm",
                                                        "data-testid": Testids.capture.tasksCompleteButton,
                                                        disabled: upsert.isPending,
                                                        onClick: ()=>upsert.mutate({
                                                                captureProjectId,
                                                                id: t.id,
                                                                title: t.title,
                                                                ownerUserId: t.ownerUserId,
                                                                dueAt: t.dueAt ? parseDateInput(dateInputValue(t.dueAt)) : null,
                                                                status: 'done',
                                                                notes: t.notes,
                                                                sortOrder: t.sortOrder
                                                            }),
                                                        iconLeft: /*#__PURE__*/ _jsx(Check, {
                                                            className: "h-3.5 w-3.5"
                                                        }),
                                                        children: "Complete"
                                                    }),
                                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                                        variant: "ghost",
                                                        size: "sm",
                                                        "data-testid": Testids.capture.tasksDeleteButton,
                                                        disabled: remove.isPending,
                                                        onClick: ()=>remove.mutate({
                                                                captureProjectId,
                                                                id: t.id
                                                            }),
                                                        iconLeft: /*#__PURE__*/ _jsx(X, {
                                                            className: "h-3.5 w-3.5"
                                                        }),
                                                        children: "Remove"
                                                    })
                                                ]
                                            })
                                        ]
                                    }, t.id))
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(SpecraCard, {
                "data-testid": Testids.capture.planPanel,
                children: [
                    /*#__PURE__*/ _jsx(SpecraCardHeader, {
                        className: "py-4",
                        children: /*#__PURE__*/ _jsx(SpecraCardTitle, {
                            children: "Capture Plan"
                        })
                    }),
                    /*#__PURE__*/ _jsxs(SpecraCardContent, {
                        className: "space-y-4",
                        children: [
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-sm text-muted-foreground",
                                children: "A rollup of this pursuit's Capture Areas, tasks, upcoming reviews and decisions — not a duplicate store. Area health is a human judgment call; completing a linked task never moves it."
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "space-y-2",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex flex-wrap items-end gap-2",
                                        children: [
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "min-w-[160px] flex-1 space-y-1",
                                                children: [
                                                    /*#__PURE__*/ _jsx("label", {
                                                        className: "text-xs font-medium",
                                                        htmlFor: "capture-plan-area-name",
                                                        children: "Area name"
                                                    }),
                                                    /*#__PURE__*/ _jsx(SpecraInput, {
                                                        id: "capture-plan-area-name",
                                                        value: areaName,
                                                        onChange: (e)=>setAreaName(e.target.value),
                                                        placeholder: "Solutioning",
                                                        "data-testid": Testids.capture.planAreaNameInput
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "w-36 space-y-1",
                                                children: [
                                                    /*#__PURE__*/ _jsx("label", {
                                                        className: "text-xs font-medium",
                                                        htmlFor: "capture-plan-area-lead",
                                                        children: "Lead"
                                                    }),
                                                    /*#__PURE__*/ _jsx(SpecraInput, {
                                                        id: "capture-plan-area-lead",
                                                        value: areaLead,
                                                        onChange: (e)=>setAreaLead(e.target.value),
                                                        placeholder: "User id",
                                                        "data-testid": Testids.capture.planAreaLeadInput
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "w-36 space-y-1",
                                                children: [
                                                    /*#__PURE__*/ _jsx("label", {
                                                        className: "text-xs font-medium",
                                                        htmlFor: "capture-plan-area-target",
                                                        children: "Target"
                                                    }),
                                                    /*#__PURE__*/ _jsx(SpecraInput, {
                                                        id: "capture-plan-area-target",
                                                        type: "date",
                                                        value: areaTarget,
                                                        onChange: (e)=>setAreaTarget(e.target.value),
                                                        "data-testid": Testids.capture.planAreaTargetInput
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "min-w-[180px] flex-1 space-y-1",
                                                children: [
                                                    /*#__PURE__*/ _jsx("label", {
                                                        className: "text-xs font-medium",
                                                        htmlFor: "capture-plan-area-summary",
                                                        children: "Summary"
                                                    }),
                                                    /*#__PURE__*/ _jsx(SpecraInput, {
                                                        id: "capture-plan-area-summary",
                                                        value: areaSummary,
                                                        onChange: (e)=>setAreaSummary(e.target.value),
                                                        placeholder: "What this area covers",
                                                        "data-testid": Testids.capture.planAreaSummaryInput
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraButton, {
                                                size: "sm",
                                                onClick: submitArea,
                                                disabled: !areaName.trim() || addArea.isPending,
                                                "data-testid": Testids.capture.planAreaAddButton,
                                                iconLeft: /*#__PURE__*/ _jsx(Plus, {
                                                    className: "h-4 w-4"
                                                }),
                                                children: "Add area"
                                            })
                                        ]
                                    }),
                                    areas.length === 0 ? /*#__PURE__*/ _jsx("p", {
                                        className: "text-sm text-muted-foreground",
                                        children: "No capture areas yet."
                                    }) : /*#__PURE__*/ _jsx("ul", {
                                        className: "space-y-2",
                                        children: areas.map((a)=>/*#__PURE__*/ _jsx(CapturePlanAreaRow, {
                                                area: a,
                                                captureProjectId: captureProjectId,
                                                tasks: tasks,
                                                correlationId: correlationId
                                            }, a.id))
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "grid gap-3 sm:grid-cols-2",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "space-y-1",
                                        "data-testid": Testids.capture.planUpcomingReviews,
                                        children: [
                                            /*#__PURE__*/ _jsx("h3", {
                                                className: "text-xs font-medium text-muted-foreground",
                                                children: "Upcoming reviews"
                                            }),
                                            upcomingReviews.length === 0 ? /*#__PURE__*/ _jsx("p", {
                                                className: "text-sm text-muted-foreground",
                                                children: "No upcoming reviews."
                                            }) : /*#__PURE__*/ _jsx("ul", {
                                                className: "space-y-1",
                                                children: upcomingReviews.map((r)=>/*#__PURE__*/ _jsxs("li", {
                                                        className: "text-sm",
                                                        children: [
                                                            r.label || r.milestoneType,
                                                            " · ",
                                                            formatTs(r.scheduledAt)
                                                        ]
                                                    }, r.id))
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "space-y-1",
                                        "data-testid": Testids.capture.planUpcomingDecisions,
                                        children: [
                                            /*#__PURE__*/ _jsx("h3", {
                                                className: "text-xs font-medium text-muted-foreground",
                                                children: "Upcoming decisions"
                                            }),
                                            upcomingDecisions.length === 0 ? /*#__PURE__*/ _jsx("p", {
                                                className: "text-sm text-muted-foreground",
                                                children: "No upcoming decisions."
                                            }) : /*#__PURE__*/ _jsx("ul", {
                                                className: "space-y-1",
                                                children: upcomingDecisions.map((d)=>/*#__PURE__*/ _jsxs("li", {
                                                        className: "text-sm",
                                                        children: [
                                                            "Due ",
                                                            formatTs(d.dueAt),
                                                            " · ",
                                                            d.notes || 'No notes'
                                                        ]
                                                    }, d.id))
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "space-y-2",
                                "data-testid": Testids.capture.planReferences,
                                children: [
                                    /*#__PURE__*/ _jsx("h3", {
                                        className: "text-xs font-medium text-muted-foreground",
                                        children: "References"
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "space-y-1",
                                        children: [
                                            /*#__PURE__*/ _jsx("p", {
                                                className: "text-xs font-semibold text-muted-foreground",
                                                children: "Partners"
                                            }),
                                            !references || references.partners.length === 0 ? /*#__PURE__*/ _jsx("p", {
                                                className: "text-sm text-muted-foreground",
                                                children: "No partners linked."
                                            }) : /*#__PURE__*/ _jsx("ul", {
                                                className: "flex flex-wrap gap-2",
                                                children: references.partners.map((p)=>/*#__PURE__*/ _jsx("li", {
                                                        className: "inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700",
                                                        children: p.companyProfileId
                                                    }, p.teamingLinkId))
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "space-y-1",
                                        children: [
                                            /*#__PURE__*/ _jsx("p", {
                                                className: "text-xs font-semibold text-muted-foreground",
                                                children: "Solutions & outputs"
                                            }),
                                            !references || references.solutionsAndOutputs.length === 0 ? /*#__PURE__*/ _jsx("p", {
                                                className: "text-sm text-muted-foreground",
                                                children: "No solution outputs linked."
                                            }) : /*#__PURE__*/ _jsx("ul", {
                                                className: "flex flex-wrap gap-2",
                                                children: references.solutionsAndOutputs.map((s)=>/*#__PURE__*/ _jsxs("li", {
                                                        className: "inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700",
                                                        children: [
                                                            s.title,
                                                            " · ",
                                                            workstreamLabel(s.workstreamType)
                                                        ]
                                                    }, s.workstreamItemId))
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "space-y-1",
                                        children: [
                                            /*#__PURE__*/ _jsx("p", {
                                                className: "text-xs font-semibold text-muted-foreground",
                                                children: "Customer"
                                            }),
                                            references?.customer?.captureProfileId ? /*#__PURE__*/ _jsx("p", {
                                                className: "text-sm",
                                                children: references.customer.customerAgencyNames || 'Customer profile'
                                            }) : /*#__PURE__*/ _jsx("p", {
                                                className: "text-sm text-muted-foreground",
                                                children: "No customer profile recorded."
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
